import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Stack,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    Chip,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';
import {
    Search,
    RefreshCw,
    Eye,
    CreditCard,
    Building2,
    Wallet,
    CheckCircle2,
    Clock,
    X
} from 'lucide-react';
import { getAllPaymentAccountsAdmin } from '../../Services/PaymentAccounts/PaymentAccountsApi';
import GlobalPagination from '../../Components/Common/Globalpagination';

// Brand Color Palette conforming to AGENTS.md
const FOREST = '#6E8964';
const SAGE_DARK = '#1B5E20';
const AMBER = '#D97706';
const AMBER_BG = '#FFFBEB';
const GREEN_BG = '#F0FDF4';
const GREEN_BORDER = '#BBF7D0';
const BORDER_COLOR = '#E2E8F0';
const BORDER_LIGHT = '#F1F5F9';
const TEXT_PRIMARY = '#1E293B';
const TEXT_SECONDARY = '#64748B';
const TEXT_MUTED = '#94A3B8';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const PaymentMethods = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Read any incoming search query from router navigation state or query params (supporting name & email keys)
    const initialSearch = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return (
            location.state?.search ||
            params.get('search') ||
            location.state?.userName ||
            location.state?.name ||
            params.get('name') ||
            location.state?.userEmail ||
            location.state?.email ||
            params.get('email') ||
            ''
        );
    }, [location.search, location.state]);

    // Data states
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Pagination states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter states
    const [searchQuery, setSearchQuery] = useState(initialSearch);
    const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
    const [selectedMethod, setSelectedMethod] = useState('all');
    const [selectedVerification, setSelectedVerification] = useState('all');

    // Request tracker to prevent race conditions from outdated async responses
    const latestRequestIdRef = useRef(0);
    const locationKeyRef = useRef(location.key);

    // Sync search only on actual router navigation
    useEffect(() => {
        if (location.key !== locationKeyRef.current) {
            locationKeyRef.current = location.key;
            const params = new URLSearchParams(location.search);
            const navSearch = (
                location.state?.search ||
                params.get('search') ||
                location.state?.userName ||
                location.state?.name ||
                params.get('name') ||
                location.state?.userEmail ||
                location.state?.email ||
                params.get('email') ||
                ''
            );
            setSearchQuery(navSearch);
            setDebouncedSearch(navSearch);
            setPage(1);
        }
    }, [location.key, location.search, location.state]);

    // Debounce search input typing (350ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (debouncedSearch !== searchQuery) {
                setDebouncedSearch(searchQuery);
                setPage(1);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [searchQuery, debouncedSearch]);

    // Fetch payment accounts using Backend Search API
    const fetchAccounts = useCallback(async (isRefresh = false) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);

            // API uses 1-based indexing for page
            const apiPage = page;
            const params = {
                page: apiPage,
                search: debouncedSearch,
                methodType: selectedMethod !== 'all' ? selectedMethod : ''
            };
            if (rowsPerPage !== 10) params.limit = rowsPerPage;
            const res = await getAllPaymentAccountsAdmin(params);

            // Prevent race conditions: ignore response if a newer request was made
            if (requestId !== latestRequestIdRef.current) {
                return;
            }

            let accountsData = [];
            let totalCount = 0;

            if (res && res.success) {
                accountsData = Array.isArray(res.data) ? res.data : (res.data?.accounts || []);
                totalCount = res.totalRecords ?? res.totalItems ?? res.total ?? res.count ?? accountsData.length;
            } else if (res && Array.isArray(res.data)) {
                accountsData = res.data;
                totalCount = res.data.length;
            } else if (res && Array.isArray(res.accounts)) {
                accountsData = res.accounts;
                totalCount = res.total ?? res.accounts.length;
            }

            // Ensure search query matching by user name or email only
            if (debouncedSearch && debouncedSearch.trim() && accountsData.length > 0) {
                const query = debouncedSearch.toLowerCase().trim();

                const matchedAccounts = accountsData.filter((account) => {
                    const userName = String(account.user?.name || account.cuName || '')
                        .toLowerCase();

                    const userEmail = String(
                        account.user?.email || account.emailOfAccountHolder || ''
                    ).toLowerCase();

                    return userName.includes(query) || userEmail.includes(query);
                });

                accountsData = matchedAccounts;
                totalCount = matchedAccounts.length;
            }

            setPaymentAccounts(accountsData);
            setTotalRecords(totalCount);
        } catch (err) {
            if (requestId === latestRequestIdRef.current) {
                console.error("Error fetching payment accounts:", err);
                const status = err.response?.status;
                if (status === 404) {
                    setPaymentAccounts([]);
                    setTotalRecords(0);
                    setError(null);
                } else {
                    setError(err.response?.data?.message || "Failed to retrieve payment accounts. Please verify your connection or session.");
                    setPaymentAccounts([]);
                    setTotalRecords(0);
                }
            }
        } finally {
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [page, rowsPerPage, debouncedSearch, selectedMethod]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    // Handle Search & Filter changes
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleMethodChange = (e) => {
        setSelectedMethod(e.target.value);
        setPage(1);
    };

    const handleVerificationChange = (e) => {
        setSelectedVerification(e.target.value);
        setPage(1);
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setDebouncedSearch('');
        setSelectedMethod('all');
        setSelectedVerification('all');
        setPage(1);
    };

    // Filter by verification status if applied
    const displayedAccounts = useMemo(() => {
        if (selectedVerification === 'verified') return paymentAccounts.filter(a => a.isVerified);
        if (selectedVerification === 'unverified') return paymentAccounts.filter(a => !a.isVerified);
        return paymentAccounts;
    }, [paymentAccounts, selectedVerification]);

    // Metrics for summary cards
    const stats = useMemo(() => {
        const total = totalRecords || paymentAccounts.length;
        const verifiedCount = paymentAccounts.filter(a => a.isVerified).length;
        const paypalCount = paymentAccounts.filter(a => String(a.methodType || '').toLowerCase().includes('paypal')).length;
        const bankCount = paymentAccounts.filter(a => String(a.methodType || '').toLowerCase().includes('bank')).length;
        return { total, verifiedCount, paypalCount, bankCount };
    }, [totalRecords, paymentAccounts]);

    // Shared input style matching project design
    const inputFieldSx = {
        bgcolor: '#FFFFFF',
        '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            height: '38px',
            fontSize: '0.8rem',
            '& fieldset': { borderColor: BORDER_COLOR },
            '&:hover fieldset': { borderColor: '#CBD5E1' },
            '&.Mui-focused fieldset': { borderColor: FOREST, borderWidth: '1px' },
        },
    };

    const selectFieldSx = {
        borderRadius: '6px',
        height: '38px',
        bgcolor: '#FFFFFF',
        fontSize: '0.8rem',
        '& fieldset': { borderColor: BORDER_COLOR },
        '&:hover fieldset': { borderColor: '#CBD5E1' },
        '&.Mui-focused fieldset': { borderColor: FOREST, borderWidth: '1px' },
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3.5, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ color: TEXT_PRIMARY, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 600, letterSpacing: '-0.3px' }}>
                        Payment Accounts
                    </Typography>
                    <Typography sx={{ color: TEXT_SECONDARY, fontSize: '0.85rem', mt: 0.25, fontWeight: 400 }}>
                        Review, audit, and inspect user & host payout settlement accounts
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                        variant="outlined"
                        startIcon={<RefreshCw size={15} className={refreshing ? "spin-icon" : ""} />}
                        onClick={() => fetchAccounts(true)}
                        disabled={refreshing || loading}
                        disableElevation disableFocusRipple disableRipple disableTouchRipple
                        sx={{
                            borderColor: BORDER_COLOR,
                            color: TEXT_SECONDARY,
                            borderRadius: '6px',
                            px: 2,
                            py: 0.75,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.825rem',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            '&:hover': { borderColor: BORDER_COLOR, bgcolor: '#FFFFFF' }
                        }}
                    >
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </Button>
                </Stack>
            </Box>

            {/* Metric Summary Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3.5 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        border: `1px solid ${BORDER_COLOR}`,
                        bgcolor: '#FFFFFF',
                        boxShadow: 'none'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Total Accounts
                        </Typography>
                        <CreditCard size={18} color={TEXT_SECONDARY} />
                    </Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: TEXT_PRIMARY, mt: 1 }}>
                        {stats.total}
                    </Typography>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        border: `1px solid ${BORDER_COLOR}`,
                        bgcolor: '#FFFFFF',
                        boxShadow: 'none'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            PayPal
                        </Typography>
                        <Wallet size={18} color="#2563EB" />
                    </Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: TEXT_PRIMARY, mt: 1 }}>
                        {stats.paypalCount}
                    </Typography>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        border: `1px solid ${BORDER_COLOR}`,
                        bgcolor: '#FFFFFF',
                        boxShadow: 'none'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Bank Transfer
                        </Typography>
                        <Building2 size={18} color={FOREST} />
                    </Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: TEXT_PRIMARY, mt: 1 }}>
                        {stats.bankCount}
                    </Typography>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        border: `1px solid ${BORDER_COLOR}`,
                        bgcolor: '#FFFFFF',
                        boxShadow: 'none'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Verified
                        </Typography>
                        <CheckCircle2 size={18} color={SAGE_DARK} />
                    </Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: TEXT_PRIMARY, mt: 1 }}>
                        {stats.verifiedCount}
                    </Typography>
                </Paper>
            </Box>

            {/* Error Notice */}
            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 3,
                        borderRadius: '8px',
                        border: '1px solid #FECACA',
                        bgcolor: '#FEF2F2',
                        color: '#991B1B',
                        fontSize: '0.825rem'
                    }}
                    action={
                        <Button color="inherit" size="small" onClick={() => fetchAccounts()}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {/* Search and Filters Bar */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: '8px',
                    border: `1px solid ${BORDER_COLOR}`,
                    bgcolor: '#FFFFFF',
                    boxShadow: 'none'
                }}
            >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Search Input */}
                    <TextField
                        placeholder="Search by name, email, bank, user ID..."
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' }, ...inputFieldSx }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={16} color={TEXT_SECONDARY} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.25 }}>
                                        <X size={14} color={TEXT_MUTED} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                    />

                    {/* Filter Dropdowns */}
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
                        {/* Method Filter */}
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                                value={selectedMethod}
                                onChange={handleMethodChange}
                                displayEmpty
                                sx={selectFieldSx}
                            >
                                <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All Methods</MenuItem>
                                <MenuItem value="paypal" sx={{ fontSize: '0.8rem' }}>PayPal</MenuItem>
                                <MenuItem value="bank_transfer" sx={{ fontSize: '0.8rem' }}>Bank Transfer</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Verification Filter */}
                        <FormControl size="small" sx={{ minWidth: 145 }}>
                            <Select
                                value={selectedVerification}
                                onChange={handleVerificationChange}
                                displayEmpty
                                sx={selectFieldSx}
                            >
                                <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All Verification</MenuItem>
                                <MenuItem value="verified" sx={{ fontSize: '0.8rem' }}>Verified Only</MenuItem>
                                <MenuItem value="unverified" sx={{ fontSize: '0.8rem' }}>Pending Verification</MenuItem>
                            </Select>
                        </FormControl>

                        {(searchQuery || selectedMethod !== 'all' || selectedVerification !== 'all') && (
                            <Button
                                size="small"
                                onClick={handleClearFilters}
                                disableElevation disableFocusRipple disableRipple disableTouchRipple
                                sx={{
                                    textTransform: 'none',
                                    color: TEXT_SECONDARY,
                                    fontSize: '0.78rem',
                                    border: `1px solid ${BORDER_COLOR}`,
                                    borderRadius: '6px',
                                    px: 1.5,
                                    '&:hover': { bgcolor: '#FFFFFF', borderColor: BORDER_COLOR }
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Paper>

            {/* Table Section */}
            <Paper elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${BORDER_COLOR}`, overflow: "hidden", bgcolor: '#FFFFFF', boxShadow: 'none' }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 1200 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: `2px solid ${BORDER_LIGHT}` }}>
                                {['S.No', 'User Name', 'Method', 'Account Email', 'Account Details', 'Verified', 'Default', 'Created By', 'Updated By', 'Created At', 'Updated At', 'Actions'].map((head) => {
                                    const isActions = head === 'Actions';
                                    return (
                                        <TableCell
                                            key={head}
                                            align={isActions ? "right" : "left"}
                                            sx={{
                                                fontWeight: 600,
                                                color: TEXT_SECONDARY,
                                                fontSize: '0.72rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px',
                                                py: 1.8,
                                                px: 2,
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {head}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={28} sx={{ color: FOREST, mb: 1 }} />
                                        <Typography sx={{ fontSize: '0.85rem', color: TEXT_SECONDARY }}>Loading payment accounts...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : displayedAccounts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                        <CreditCard size={32} color={TEXT_MUTED} style={{ marginBottom: 8 }} />
                                        <Typography sx={{ fontSize: '0.9rem', color: TEXT_PRIMARY, fontWeight: 500 }}>
                                            {debouncedSearch ? 'No matching records found' : 'No payment accounts found'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.8rem', color: TEXT_SECONDARY, mt: 0.5 }}>
                                            {debouncedSearch || selectedMethod !== 'all' || selectedVerification !== 'all'
                                                ? 'Try adjusting your search criteria or filters.'
                                                : 'No registered payment accounts are available in the system.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                displayedAccounts.map((row, index) => {
                                    const isRowPaypal = String(row.methodType || '').toLowerCase().includes('paypal');
                                    const isRowBank = String(row.methodType || '').toLowerCase().includes('bank');

                                    return (
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                bgcolor: '#FFFFFF',
                                                '& td': { borderBottom: `1px solid ${BORDER_LIGHT}` },
                                                '&:last-child td': { borderBottom: 0 }
                                            }}
                                        >
                                            {/* S.No */}
                                            <TableCell sx={{ py: 2, px: 2, fontSize: '0.85rem', color: TEXT_PRIMARY, fontWeight: 500 }}>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>

                                            {/* User Name */}
                                            <TableCell sx={{ py: 2, px: 2, fontSize: '0.85rem', fontWeight: 500, color: TEXT_PRIMARY }}>
                                                {row.user?.name || row.cuName || '—'}
                                            </TableCell>

                                            {/* Method */}
                                            <TableCell sx={{ py: 2, px: 2 }}>
                                                <Chip
                                                    size="small"
                                                    icon={isRowPaypal ? <Wallet size={12} color="#1D4ED8" /> : isRowBank ? <Building2 size={12} color={SAGE_DARK} /> : <CreditCard size={12} />}
                                                    label={row.methodType ? String(row.methodType).toUpperCase() : 'N/A'}
                                                    sx={{
                                                        bgcolor: isRowPaypal ? '#EFF6FF' : isRowBank ? '#F0FDF4' : '#F8FAFC',
                                                        color: isRowPaypal ? '#1D4ED8' : isRowBank ? SAGE_DARK : TEXT_PRIMARY,
                                                        border: `1px solid ${isRowPaypal ? '#BFDBFE' : isRowBank ? GREEN_BORDER : BORDER_COLOR}`,
                                                        fontWeight: 500,
                                                        fontSize: '0.72rem',
                                                        height: 24,
                                                        '& .MuiChip-icon': { ml: 0.5 }
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Account Email */}
                                            <TableCell sx={{ py: 2, px: 2, fontSize: '0.825rem', color: TEXT_PRIMARY, fontWeight: 400 }}>
                                                {row.emailOfAccountHolder || '—'}
                                            </TableCell>

                                            {/* Account Details (Holder Name, Bank Name, Account Number) */}
                                            <TableCell sx={{ py: 2, px: 2 }}>
                                                {row.accountHolderName || row.bankName || row.accountNumber ? (
                                                    <Box>
                                                        {row.accountHolderName && (
                                                             <Typography sx={{ fontSize: '0.825rem', color: TEXT_PRIMARY, fontWeight: 500 }}>
                                                                {row.accountHolderName}
                                                            </Typography>
                                                        )}
                                                        {row.bankName && (
                                                            <Typography sx={{ fontSize: '0.78rem', color: TEXT_SECONDARY }}>
                                                                {row.bankName}
                                                            </Typography>
                                                        )}
                                                        {row.accountNumber && (
                                                            <Typography sx={{ fontSize: '0.72rem', color: TEXT_MUTED, fontFamily: 'monospace' }}>
                                                                A/C: {row.accountNumber}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Typography sx={{ fontSize: '0.825rem', color: TEXT_MUTED }}>—</Typography>
                                                )}
                                            </TableCell>

                                            {/* Verified Status */}
                                            <TableCell sx={{ py: 2, px: 2 }}>
                                                <Chip
                                                    size="small"
                                                    icon={row.isVerified ? <CheckCircle2 size={12} color={SAGE_DARK} /> : <Clock size={12} color={AMBER} />}
                                                    label={row.isVerified ? "Verified" : "Pending"}
                                                    sx={{
                                                        bgcolor: row.isVerified ? GREEN_BG : AMBER_BG,
                                                        color: row.isVerified ? SAGE_DARK : AMBER,
                                                        border: `1px solid ${row.isVerified ? GREEN_BORDER : '#FDE68A'}`,
                                                        fontWeight: 500,
                                                        fontSize: '0.72rem',
                                                        height: 22,
                                                        '& .MuiChip-icon': { ml: 0.5 }
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Default */}
                                            <TableCell sx={{ py: 2, px: 2 }}>
                                                {row.isDefault ? (
                                                    <Chip
                                                        size="small"
                                                        label="Primary"
                                                        sx={{
                                                             bgcolor: AMBER_BG,
                                                            color: AMBER,
                                                            border: '1px solid #FDE68A',
                                                            fontWeight: 500,
                                                            fontSize: '0.7rem',
                                                            height: 20
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontSize: '0.75rem', color: TEXT_MUTED }}>No</Typography>
                                                )}
                                            </TableCell>

                                            {/* Created By */}
                                            <TableCell sx={{ py: 2, px: 2, fontSize: '0.825rem', color: TEXT_SECONDARY, whiteSpace: 'nowrap' }}>
                                                {row.cuName || row.user?.name || '-'}
                                            </TableCell>

                                            {/* Updated By */}
                                            <TableCell sx={{ py: 2, px: 2, fontSize: '0.825rem', color: TEXT_SECONDARY, whiteSpace: 'nowrap' }}>
                                                {row.uuName || '-'}
                                            </TableCell>

                                            {/* Created At */}
                                            <TableCell sx={{ py: 2, px: 2, fontSize: '0.825rem', color: TEXT_SECONDARY, whiteSpace: 'nowrap' }}>
                                                {formatDate(row.createdAt)}
                                            </TableCell>

                                            {/* Updated At */}
                                            <TableCell sx={{ py: 2, px: 2, fontSize: '0.825rem', color: TEXT_SECONDARY, whiteSpace: 'nowrap' }}>
                                                {formatDate(row.updatedAt)}
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="right" sx={{ py: 2, px: 2 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                    <Tooltip title="View Account Details">
                                                        <Button
                                                            size="small"
                                                            startIcon={<Eye size={14} />}
                                                            onClick={() => navigate(`/payment-accounts/view-details/${row.id}`, { state: { account: row } })}
                                                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                                                            sx={{
                                                                textTransform: 'none',
                                                                color: TEXT_PRIMARY,
                                                                fontSize: '0.78rem',
                                                                fontWeight: 500,
                                                                border: `1px solid ${BORDER_COLOR}`,
                                                                borderRadius: '6px',
                                                                px: 1.5,
                                                                py: 0.4,
                                                                bgcolor: '#FFFFFF',
                                                                '&:hover': { bgcolor: '#FFFFFF', borderColor: BORDER_COLOR }
                                                            }}
                                                        >
                                                            View
                                                        </Button>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Table Pagination */}
                <GlobalPagination
                    page={page}
                    pageSize={rowsPerPage}
                    totalRecords={displayedAccounts.length === 0 ? 0 : (totalRecords || displayedAccounts.length)}
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newSize) => {
                        setRowsPerPage(newSize);
                        setPage(1);
                    }}
                    loading={loading || refreshing}
                />
            </Paper>
        </Box>
    );
};

export default PaymentMethods;
